import type { App } from 'vue'
import { Field, Form, ErrorMessage, defineRule, configure } from 'vee-validate'
import { required, email, regex, between, min, min_value, max_value, confirmed } from '@vee-validate/rules'
import { ValidateRules } from '@/util/ValidateRules'

interface ValidationContext {
  field: string
  rule?: {
    name: string
    params?: unknown[] | Record<string, unknown>
  }
}

export function setupValidation(app: App): void {
  // Configure vee-validate
  configure({
    generateMessage: (ctx: ValidationContext) => {
      const params = Array.isArray(ctx.rule?.params) ? ctx.rule.params as string[] : undefined
      const messages: Record<string, string> = {
        required: `The ${ctx.field} field is required`,
        email: `The ${ctx.field} field must be a valid email`,
        min: `The ${ctx.field} field must be at least ${params?.[0] || ''} characters`,
        confirmed: `The ${ctx.field} field confirmation does not match`,
        between: `The ${ctx.field} field must be between ${params?.[0] || ''} and ${params?.[1] || ''}`,
        regex: `The ${ctx.field} field format is invalid`,
        min_value: `The ${ctx.field} field must be ${params?.[0] || ''} or more`,
        max_value: `The ${ctx.field} field must be ${params?.[0] || ''} or less`,
      }
      return messages[ctx.rule?.name || ''] || `The ${ctx.field} field is invalid`
    },
  })

  // Define validation rules
  defineRule('required', required)
  defineRule('email', email)
  defineRule('regex', regex)
  defineRule('between', between)
  defineRule('min', min)
  defineRule('min_value', min_value)
  defineRule('max_value', max_value)
  defineRule('confirmed', confirmed)
  
  // Custom validation rules
  defineRule('url', (value: string) => ValidateRules.url.validate(value) || ValidateRules.url.message)
  defineRule('searchTermCsv', (value: string) => ValidateRules.searchTermCsv.validate(value) || ValidateRules.searchTermCsv.message)
  defineRule('validNewEntepriseId', (value: string) => ValidateRules.validNewEntepriseId.validate(value) || ValidateRules.validNewEntepriseId.message)
  defineRule('possibleEnterpriseId', (value: string, params: unknown) => ValidateRules.possibleEnterpriseId.validate(value, params as { curEntIdList: string[] }) || ValidateRules.possibleEnterpriseId.message)
  defineRule('validSubscriberId', (value: string) => ValidateRules.validSubscriberId.validate(value) || ValidateRules.validSubscriberId.message)
  defineRule('validSupplierId', (value: string) => ValidateRules.validSupplierId.validate(value) || ValidateRules.validSupplierId.message)
  defineRule('validEnrolId', (value: string) => ValidateRules.validEnrolId.validate(value) || ValidateRules.validEnrolId.message)
  defineRule('nonRepeatedId', (value: string, params: unknown) => ValidateRules.nonRepeatedId.validate(value, params as { curIdList: string[] }) || ValidateRules.nonRepeatedId.message)

  // Register global components
  app.component('Field', Field)
  app.component('VeeForm', Form)
  app.component('ErrorMessage', ErrorMessage)
}